import { FC } from 'react'
import { StyledButton } from 'Components/Buttons/StyledButton'

import { ButtonContainer } from 'Utils/styles/spacing'
import { UserDetailsForEmployer } from 'Utils/types/user'
import { UserInformationUpdate } from 'Services/apiResponseTypes'
import { EmployerMetaField } from 'Services/employerService'
import { FormTextInput } from 'Components/Inputs/FormTextInput'
import { ModalBody } from 'Components/Modal/ModalBody'
import { TextLink } from 'Components/ExternalLink/TextLink'
import { RegisterOptions, useForm, useWatch } from 'react-hook-form'
import { useEmployerInfoContext } from 'Contexts/EmployerInfoContext'
import { useUpdateUserInformation } from 'Hooks/api/employer/userManagement/useUpdateUserInformation'
import { useUserMetadataForm } from 'Hooks/useUserMetadataForm'

const nameRules = (savedName: string): RegisterOptions => ({
  validate: (value: string) => value === savedName || !!value.trim()
})

type ManageUserInformationValues = {
  firstName: string
  lastName: string
  [metaFieldId: string]: string
}

type UserInformationFormProps = {
  user: UserDetailsForEmployer
  employerId: number
  metaFields: EmployerMetaField[]
  metadataDefaults: Record<string, string>
  metadataRules: Record<string, RegisterOptions>
  isJoonAdmin: boolean
}
const UserInformationForm: FC<UserInformationFormProps> = ({
  user,
  employerId,
  metaFields,
  metadataDefaults,
  metadataRules,
  isJoonAdmin
}) => {
  const savedFirstName = user.userInfo.firstName ?? ''
  const savedLastName = user.userInfo.lastName ?? ''

  const {
    control,
    register,
    getValues,
    formState: { errors, isValid, isDirty, dirtyFields }
  } = useForm<ManageUserInformationValues>({
    defaultValues: { firstName: savedFirstName, lastName: savedLastName, ...metadataDefaults },
    mode: 'onChange'
  })

  const { mutate: updateUserInformation, isLoading: isUpdating } = useUpdateUserInformation()
  const values = useWatch({ control })

  const onUpdate = () => {
    const formValues = getValues()
    const update: UserInformationUpdate = { user_id: user.id }

    if (dirtyFields.firstName || dirtyFields.lastName) {
      update.user_fields = {
        ...(dirtyFields.firstName && { first_name: formValues.firstName }),
        ...(dirtyFields.lastName && { last_name: formValues.lastName })
      }
    }

    const changedMetaFields = metaFields.filter(({ id }) => dirtyFields[`${id}`])
    if (changedMetaFields.length) {
      update.metadata = Object.fromEntries(
        changedMetaFields.map(({ id, fieldName }) => [fieldName, formValues[`${id}`]])
      )
    }

    updateUserInformation({ employerId, updates: [update] })
  }

  return (
    <>
      <ModalBody>
        <FormTextInput
          fieldName="firstName"
          errorPresent={!!errors.firstName}
          labelId="firstName.label"
          register={register}
          registerOptions={nameRules(savedFirstName)}
        />
        <FormTextInput
          fieldName="lastName"
          errorPresent={!!errors.lastName}
          labelId="lastName.label"
          register={register}
          registerOptions={nameRules(savedLastName)}
        />
        {metaFields.map(({ fieldName, id, required }) => (
          <FormTextInput
            key={id}
            fieldName={`${id}`}
            errorPresent={
              !!(errors && errors[`${id}`] && errors[`${id}`]!.message) ||
              (required && !values[`${id}`])
            }
            label={fieldName}
            register={register}
            registerOptions={metadataRules[`${id}`]}
            required={required}
          />
        ))}
      </ModalBody>

      {!isJoonAdmin && (
        <span>
          To update a user&apos;s email address, please contact{' '}
          <TextLink text="info@joon.io" href="mailto:info@joon.io" target="_blank" />.
        </span>
      )}

      <ButtonContainer>
        <StyledButton
          primary
          id="permissionsChange.update"
          onClick={onUpdate}
          disabled={isUpdating || !isValid || !isDirty}
        />
      </ButtonContainer>
    </>
  )
}

type ManageUserInformationFormProps = {
  user?: UserDetailsForEmployer
  isJoonAdmin?: boolean
}
export const ManageUserInformationForm: FC<ManageUserInformationFormProps> = ({
  user,
  isJoonAdmin = false
}) => {
  const {
    state: { employerId }
  } = useEmployerInfoContext()

  const { metaFields, isLoadingMetaFields, defaultValues, registerOptions } = useUserMetadataForm({
    employerId,
    user
  })

  if (!user || !employerId) return null
  if (isLoadingMetaFields) return <span>Loading...</span>

  return (
    <UserInformationForm
      user={user}
      employerId={employerId}
      metaFields={metaFields}
      metadataDefaults={defaultValues}
      metadataRules={registerOptions}
      isJoonAdmin={isJoonAdmin}
    />
  )
}
